import { useState, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  Eye,
  EyeOff,
  LogIn,
  Shield,
  TrendingUp,
  Users,
  Wallet,
  Lock,
  Mail,
  Sparkles,
  Zap,
  CheckCircle2,
  Fingerprint,
} from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login, verify2FA, user, initializing } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [focus, setFocus] = useState(null);
  const [clock, setClock] = useState("");

  // 2FA challenge state
  const [challenge, setChallenge] = useState(null);
  const [otp, setOtp] = useState("");
  const [useBackup, setUseBackup] = useState(false);
  const [backupCode, setBackupCode] = useState("");

  useEffect(() => {
    const t = () =>
      setClock(
        new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        })
      );
    t();
    const i = setInterval(t, 30000);
    return () => clearInterval(i);
  }, []);

  if (!initializing && user)
    return <Navigate to="/root/admin/dashboard" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await login(email, password);
      if (res?.requires_2fa) {
        setChallenge({ challenge_token: res.challenge_token, email: res.email || email });
        toast.info("Enter the 6-digit code from your authenticator app");
      } else {
        toast.success("Welcome back, admin");
        navigate("/root/admin/dashboard");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Invalid credentials";
      toast.error(typeof msg === "string" ? msg : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handle2FA = async (e) => {
    e.preventDefault();
    const code = useBackup ? backupCode.trim().toUpperCase() : otp.trim();
    if (!code) return toast.error(useBackup ? "Enter backup code" : "Enter 6-digit code");
    setLoading(true);
    try {
      await verify2FA({
        challenge_token: challenge.challenge_token,
        totp_code: useBackup ? "" : code,
        backup_code: useBackup ? code : "",
      });
      toast.success("2FA verified — welcome back");
      navigate("/root/admin/dashboard");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Invalid code";
      toast.error(typeof msg === "string" ? msg : "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen w-full grid lg:grid-cols-[1.05fr_1fr] text-white relative overflow-hidden"
      data-testid="login-page"
      style={{ background: "#060a10" }}
    >
      {/* ============= AMBIENT BACKGROUND ============= */}
      {/* Animated orbs */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-48 -left-48 h-[640px] w-[640px] rounded-full blur-[120px]"
        style={{
          background:
            "radial-gradient(circle, rgba(25,165,92,0.32), rgba(25,165,92,0) 60%)",
          animation: "floatOrbA 14s ease-in-out infinite",
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-48 -right-48 h-[640px] w-[640px] rounded-full blur-[120px]"
        style={{
          background:
            "radial-gradient(circle, rgba(212,160,18,0.26), rgba(212,160,18,0) 60%)",
          animation: "floatOrbB 16s ease-in-out infinite",
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[400px] w-[400px] rounded-full blur-[100px] opacity-30"
        style={{
          background:
            "radial-gradient(circle, rgba(59,130,246,0.18), transparent 60%)",
        }}
      />

      {/* Grid */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.055]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.8) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.8) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
          maskImage:
            "radial-gradient(ellipse at center, black 25%, transparent 75%)",
        }}
      />

      {/* ============= LEFT: HERO ============= */}
      <div className="hidden lg:flex relative flex-col justify-between p-12 xl:p-16">
        {/* Brand */}
        <div className="flex items-center gap-3 relative z-10">
          <div
            className="h-12 w-12 rounded-2xl flex items-center justify-center font-display font-extrabold text-lg relative"
            style={{
              background: "linear-gradient(135deg, #dc2626 0%, #7f1d1d 100%)",
              boxShadow:
                "0 10px 28px rgba(220,38,38,0.45), inset 0 1px 0 rgba(255,255,255,0.25)",
            }}
          >
            <span className="text-white">M</span>
            <span style={{ color: "#fecaca" }}>8</span>
            <span
              className="absolute -bottom-1 -right-1 h-4 w-4 rounded-full flex items-center justify-center"
              style={{
                background:
                  "linear-gradient(135deg, #34d399 0%, #047857 100%)",
              }}
            >
              <Sparkles size={8} className="text-white" />
            </span>
          </div>
          <div>
            <div className="font-display font-extrabold text-[20px] tracking-tight leading-none">
              Maxlive88 Admin
            </div>
            <div className="text-[11px] uppercase tracking-[0.24em] text-[#19a55c] mt-1.5 font-bold">
              Command Center
            </div>
          </div>
          <div className="ml-auto inline-flex items-center gap-2 px-3 h-8 rounded-full bg-white/[0.04] border border-white/10 text-[11px] font-mono text-white/60">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
            {clock}
          </div>
        </div>

        {/* Hero copy */}
        <div className="relative z-10 max-w-2xl">
          <div
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-emerald-500/25 backdrop-blur-sm mb-6"
            style={{ background: "rgba(25,165,92,0.08)" }}
          >
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-[10px] uppercase tracking-[0.28em] text-emerald-300 font-bold">
              Secure · v1.0 · All systems operational
            </span>
          </div>

          <h2 className="font-display text-[44px] xl:text-[56px] font-extrabold leading-[1.02] tracking-tight">
            Run your sportsbook with
            <span className="block mt-2">
              <span
                style={{
                  backgroundImage:
                    "linear-gradient(135deg,#fecaca 0%,#dc2626 45%,#7f1d1d 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  textShadow: "0 2px 20px rgba(220,38,38,0.18)",
                }}
              >
                surgical precision.
              </span>
            </span>
          </h2>

          <p className="text-[15px] text-white/55 mt-6 max-w-lg leading-relaxed">
            Players, deposits, withdrawals, bets, agents &amp; affiliates —
            orchestrated from a single, elegant control surface built for
            operators who care about <span className="text-white/80">every detail</span>.
          </p>

          {/* Feature chips with subtle hover */}
          <div className="flex flex-wrap gap-2 mt-7">
            {[
              { icon: <Shield size={13} />, label: "2FA Ready", tone: "emerald" },
              { icon: <TrendingUp size={13} />, label: "Live Reports", tone: "amber" },
              { icon: <Wallet size={13} />, label: "Payment Ops", tone: "blue" },
              { icon: <Users size={13} />, label: "Agent Network", tone: "violet" },
            ].map((c) => (
              <span
                key={c.label}
                className="group inline-flex items-center gap-1.5 px-3 h-8 rounded-full border border-white/10 bg-white/[0.035] text-[12px] text-white/80 font-semibold backdrop-blur-sm hover:border-white/25 hover:bg-white/[0.06] transition-colors"
              >
                <span
                  style={{
                    color:
                      c.tone === "emerald"
                        ? "#34d399"
                        : c.tone === "amber"
                        ? "#fbbf24"
                        : c.tone === "blue"
                        ? "#60a5fa"
                        : "#c084fc",
                  }}
                >
                  {c.icon}
                </span>
                {c.label}
              </span>
            ))}
          </div>
        </div>

        {/* Stats with a subtle divider line */}
        <div className="relative z-10 pt-6">
          <div
            aria-hidden
            className="h-px mb-6 relative"
            style={{
              background:
                "linear-gradient(90deg, transparent, rgba(255,255,255,0.12) 40%, rgba(255,255,255,0.12) 60%, transparent)",
            }}
          />
          <div className="grid grid-cols-3 gap-8">
            <Stat value="9,000+" label="Players onboarded" trend="+12%" />
            <Stat value="৳100Cr" label="Deposits processed" trend="+8%" />
            <Stat value="1,000" label="Agents network" trend="+24%" />
          </div>
        </div>
      </div>

      {/* ============= RIGHT: FORM ============= */}
      <div className="flex items-center justify-center p-6 sm:p-10 lg:p-14 relative z-10">
        {challenge ? (
          <form
            onSubmit={handle2FA}
            className="w-full max-w-[440px] rounded-3xl p-8 sm:p-10 relative fade-in-up"
            data-testid="admin-2fa-form"
            style={{
              background: "linear-gradient(180deg, rgba(22,30,42,0.88) 0%, rgba(11,17,26,0.94) 100%)",
              border: "1px solid rgba(255,255,255,0.08)",
              boxShadow: "0 40px 100px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.06)",
              backdropFilter: "blur(20px)",
            }}
          >
            <div className="flex items-center gap-2 mb-2">
              <div className="h-6 w-6 rounded-md flex items-center justify-center bg-emerald-500/15 border border-emerald-500/30">
                <Shield size={12} className="text-emerald-400" />
              </div>
              <span className="text-[11px] uppercase tracking-[0.26em] text-emerald-400 font-bold">
                Two-factor verification
              </span>
            </div>
            <h1 className="font-display text-[30px] font-extrabold leading-[1.05] text-white">
              Enter your 6-digit code
            </h1>
            <p className="text-[13px] text-white/55 mt-2 mb-6 leading-relaxed">
              <span className="text-emerald-300 font-semibold">{challenge.email}</span> — open your authenticator app and enter the current 6-digit code.
            </p>

            {!useBackup ? (
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="000000"
                maxLength={6}
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                className="w-full h-14 mb-4 bg-[#0a1119] border border-white/10 rounded-xl text-center text-3xl tracking-[0.55em] font-mono text-white outline-none focus:border-emerald-500/55 focus:ring-4 focus:ring-emerald-500/15"
                autoFocus
                data-testid="admin-2fa-code"
              />
            ) : (
              <input
                type="text"
                placeholder="XXXXX-XXXXX"
                maxLength={11}
                value={backupCode}
                onChange={(e) => setBackupCode(e.target.value.toUpperCase())}
                className="w-full h-14 mb-4 bg-[#0a1119] border border-white/10 rounded-xl text-center text-xl tracking-widest font-mono uppercase text-white outline-none focus:border-emerald-500/55 focus:ring-4 focus:ring-emerald-500/15"
                autoFocus
                data-testid="admin-2fa-backup"
              />
            )}

            <button
              type="button"
              onClick={() => { setUseBackup((v) => !v); setOtp(""); setBackupCode(""); }}
              className="w-full mb-5 inline-flex items-center justify-center gap-2 text-[12px] text-emerald-300/85 hover:text-emerald-200 font-semibold"
              data-testid="admin-2fa-toggle-backup"
            >
              <Fingerprint size={13} />
              {useBackup ? "Use authenticator code instead" : "Use a backup code"}
            </button>

            <button
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-xl font-bold text-[14px] flex items-center justify-center gap-2 disabled:opacity-60"
              style={{
                background: "linear-gradient(180deg, #21c46e 0%, #19a55c 55%, #0f7a40 100%)",
                boxShadow: "0 12px 32px rgba(25,165,92,0.5), inset 0 1px 0 rgba(255,255,255,0.25)",
                color: "white",
              }}
              data-testid="admin-2fa-submit"
            >
              {loading ? (
                <><div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" /><span>Verifying…</span></>
              ) : (
                <><CheckCircle2 size={16} /><span>Verify & continue</span></>
              )}
            </button>

            <button
              type="button"
              onClick={() => { setChallenge(null); setOtp(""); setBackupCode(""); }}
              className="w-full mt-4 text-[11px] text-white/40 hover:text-white/70"
            >
              ← Use a different account
            </button>
          </form>
        ) : (
        <form
          onSubmit={handleSubmit}
          className="w-full max-w-[440px] rounded-3xl p-8 sm:p-10 relative fade-in-up"
          data-testid="login-form"
          style={{
            background:
              "linear-gradient(180deg, rgba(22,30,42,0.88) 0%, rgba(11,17,26,0.94) 100%)",
            border: "1px solid rgba(255,255,255,0.08)",
            boxShadow:
              "0 40px 100px rgba(0,0,0,0.6), 0 0 0 1px rgba(25,165,92,0.08), inset 0 1px 0 rgba(255,255,255,0.06)",
            backdropFilter: "blur(20px)",
          }}
        >
          {/* Gold top accent + green bottom accent */}
          <div
            aria-hidden
            className="absolute top-0 left-12 right-12 h-[2px] rounded-b-full"
            style={{
              background:
                "linear-gradient(90deg, transparent 0%, #d4a012 30%, #ffe27a 50%, #d4a012 70%, transparent 100%)",
              boxShadow: "0 0 20px rgba(212,160,18,0.5)",
            }}
          />
          <div
            aria-hidden
            className="absolute bottom-0 left-16 right-16 h-[1px] rounded-t-full opacity-60"
            style={{
              background:
                "linear-gradient(90deg, transparent, #19a55c, transparent)",
            }}
          />

          {/* Corner sparkle accents */}
          <div
            aria-hidden
            className="absolute top-4 right-4 h-2 w-2 rounded-full bg-emerald-400"
            style={{
              boxShadow: "0 0 12px rgba(25,165,92,0.8)",
              animation: "pulse 2s ease-in-out infinite",
            }}
          />

          {/* Mobile brand */}
          <div className="lg:hidden flex items-center gap-2.5 mb-7">
            <div
              className="h-11 w-11 rounded-xl flex items-center justify-center font-display font-extrabold"
              style={{
                background:
                  "linear-gradient(135deg, #19a55c 0%, #0f7a40 100%)",
                boxShadow: "0 6px 16px rgba(25,165,92,0.4)",
              }}
            >
              <span className="text-white">M</span>
              <span style={{ color: "#ffd94a" }}>8</span>
            </div>
            <div>
              <div className="font-display font-extrabold text-lg leading-tight">
                Maxlive88 Admin
              </div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-400 font-bold">
                Command Center
              </div>
            </div>
          </div>

          {/* Headline */}
          <div className="flex items-center gap-2 mb-2">
            <div
              className="h-6 w-6 rounded-md flex items-center justify-center"
              style={{
                background:
                  "linear-gradient(135deg, rgba(25,165,92,0.22) 0%, rgba(25,165,92,0.08) 100%)",
                border: "1px solid rgba(25,165,92,0.3)",
              }}
            >
              <Fingerprint size={12} className="text-emerald-400" />
            </div>
            <span className="text-[11px] uppercase tracking-[0.26em] text-emerald-400 font-bold">
              Secure Sign in
            </span>
          </div>
          <h1 className="font-display text-[34px] sm:text-[38px] font-extrabold leading-[1.05] text-white">
            Admin access
          </h1>
          <p className="text-[13px] text-white/55 mt-2 mb-8 leading-relaxed">
            Enter your credentials to continue to the control panel.
          </p>

          {/* Email */}
          <Field
            id="email"
            label="Email"
            icon={<Mail size={15} />}
            focused={focus === "email"}
            filled={!!email}
          >
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onFocus={() => setFocus("email")}
              onBlur={() => setFocus(null)}
              className="w-full h-12 pl-10 pr-3 bg-transparent text-sm text-white placeholder:text-white/25 outline-none"
              placeholder="admin@maxlive88.com"
              required
              autoComplete="email"
              data-testid="login-email"
            />
          </Field>

          {/* Password */}
          <Field
            id="password"
            label="Password"
            icon={<Lock size={15} />}
            focused={focus === "password"}
            filled={!!password}
          >
            <input
              id="password"
              type={showPw ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onFocus={() => setFocus("password")}
              onBlur={() => setFocus(null)}
              className="w-full h-12 pl-10 pr-11 bg-transparent text-sm text-white placeholder:text-white/25 outline-none"
              placeholder="Enter your password"
              required
              autoComplete="current-password"
              data-testid="login-password"
            />
            <button
              type="button"
              onClick={() => setShowPw((v) => !v)}
              className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 flex items-center justify-center text-white/40 hover:text-white/85 transition-colors rounded-md hover:bg-white/5"
              data-testid="login-toggle-password"
              aria-label="Toggle password visibility"
              tabIndex={-1}
            >
              {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
            </button>
          </Field>

          {/* Tiny security indicator */}
          <div className="flex items-center justify-between mb-5">
            <div className="inline-flex items-center gap-1.5 text-[11px] text-white/45">
              <CheckCircle2 size={11} className="text-emerald-400" />
              Encrypted session · TLS 1.3
            </div>
            <button
              type="button"
              className="text-[11px] text-emerald-400/80 hover:text-emerald-300 font-semibold"
              onClick={() =>
                toast.info("Please contact a super-admin to reset credentials.")
              }
            >
              Need help?
            </button>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className="group w-full h-12 rounded-xl font-bold text-[14px] flex items-center justify-center gap-2 transition-transform active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed relative overflow-hidden"
            style={{
              background:
                "linear-gradient(180deg, #21c46e 0%, #19a55c 55%, #0f7a40 100%)",
              boxShadow:
                "0 12px 32px rgba(25,165,92,0.5), inset 0 1px 0 rgba(255,255,255,0.25)",
              color: "white",
            }}
            data-testid="login-submit"
          >
            {/* subtle moving sheen */}
            <span
              aria-hidden
              className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity"
              style={{
                background:
                  "linear-gradient(90deg, transparent 30%, rgba(255,255,255,0.15) 50%, transparent 70%)",
                animation: "btnShine 1.6s linear infinite",
              }}
            />
            {loading ? (
              <>
                <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Signing in...</span>
              </>
            ) : (
              <>
                <LogIn size={16} />
                <span>Sign in to dashboard</span>
                <Zap
                  size={13}
                  className="opacity-0 group-hover:opacity-100 transition-opacity"
                />
              </>
            )}
          </button>

          {/* Footer line */}
          <div className="mt-6 pt-5 border-t border-white/5 flex items-center justify-center gap-2 text-[11px] text-white/40">
            <Shield size={11} className="text-emerald-400" />
            Protected by rate limiting &amp; audit logs
          </div>
        </form>
        )}
      </div>

      <style>{`
        @keyframes floatOrbA {
          0%,100% { transform: translate(0,0); }
          50%     { transform: translate(40px,30px); }
        }
        @keyframes floatOrbB {
          0%,100% { transform: translate(0,0); }
          50%     { transform: translate(-30px,-40px); }
        }
        @keyframes btnShine {
          0%   { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        @keyframes pulse {
          0%,100% { opacity: 0.6; transform: scale(1); }
          50%     { opacity: 1;   transform: scale(1.4); }
        }
        .fade-in-up { animation: fadeUp 500ms cubic-bezier(0.2,0.9,0.3,1.0) both; }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

/* ---------- sub components ---------- */

function Field({ label, icon, focused, filled, children }) {
  const active = focused || filled;
  return (
    <div className="mb-5">
      <label
        className={`block text-[11px] font-bold uppercase tracking-[0.2em] mb-2 transition-colors ${
          focused ? "text-emerald-400" : "text-white/55"
        }`}
      >
        {label}
      </label>
      <div
        className="relative rounded-xl transition-all"
        style={{
          background: focused ? "rgba(25,165,92,0.04)" : "rgba(10,17,25,0.8)",
          border: focused
            ? "1px solid rgba(25,165,92,0.55)"
            : "1px solid rgba(255,255,255,0.08)",
          boxShadow: focused
            ? "0 0 0 4px rgba(25,165,92,0.12)"
            : "inset 0 1px 0 rgba(255,255,255,0.02)",
        }}
      >
        <span
          className={`absolute left-3 top-1/2 -translate-y-1/2 transition-colors ${
            active ? "text-emerald-400" : "text-white/35"
          }`}
        >
          {icon}
        </span>
        {children}
      </div>
    </div>
  );
}

function Stat({ value, label, trend }) {
  return (
    <div>
      <div className="flex items-baseline gap-2">
        <div
          className="font-display font-black text-[26px] xl:text-[30px] leading-none tracking-tight"
          style={{
            backgroundImage:
              "linear-gradient(180deg, #ffffff 0%, #b6c2d3 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          {value}
        </div>
        {trend && (
          <span className="text-[10px] font-extrabold text-emerald-400 inline-flex items-center gap-0.5">
            <TrendingUp size={9} /> {trend}
          </span>
        )}
      </div>
      <div className="mt-1.5 text-[11px] text-white/45 uppercase tracking-[0.15em] font-semibold">
        {label}
      </div>
    </div>
  );
}
